import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type MessagePayload,
} from 'firebase/messaging';
import { firebaseApp } from './firebase';
import { registerAdminPushTokenCallable, unregisterAdminPushTokenCallable } from './callables';
import { trackEvents } from './events';
import { reportNonFatal, serializeError } from '@/lib/errorReport';

// Controlador de web push (FCM) do console super-admin (admin.etiquetachef.com).
// Sem React. Sem tenant — super-admins não são membros de tenant; o token vai
// para `/adminPushTokens/{token}`. O servidor faz fan-out para esses tokens
// desde 6d8cac847 (2026-06-19, `notifyAdmins` → `apps/functions/src/lib/webPush.ts`);
// o comentário anterior dizia o contrário (corrigido em #926).

const TAG = 'admin/web-push';

const PREF_KEY = 'etiquetachef.admin.webPush.enabled';
const LAST_TOKEN_KEY = 'etiquetachef.admin.webPush.token';

const VAPID_KEY = ((import.meta.env as Record<string, string | undefined>).VITE_FIREBASE_VAPID_KEY ?? '');

/** Permissão do navegador para notificações, ou `unsupported` sem API/VAPID. */
export type PushPermission = NotificationPermission | 'unsupported';

/** `localStorage.getItem` tolerante a storage bloqueado (modo privado). */
function lsGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
/** `localStorage` set/remove tolerante a storage bloqueado; `null` remove. */
function lsSet(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // localStorage indisponível — segue em memória só.
  }
}

let supportCache: boolean | null = null;
let messagingInstance: ReturnType<typeof getMessaging> | null = null;

/** `true` quando o build tem `VITE_FIREBASE_VAPID_KEY`. */
export function isWebPushConfigured(): boolean {
  return VAPID_KEY.length > 0;
}

/** `true` se o navegador suporta web push E a chave VAPID está configurada. */
export async function isWebPushSupported(): Promise<boolean> {
  if (!isWebPushConfigured()) return false;
  if (supportCache !== null) return supportCache;
  try {
    supportCache = await isSupported();
  } catch (err) {
    console.warn(`${TAG}: isSupported() lançou`, err);
    // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
    reportNonFatal('admin_web_push_controller_failed', {
      site: 'isSupported() lançou',
      code: (err as { code?: string }).code ?? null,
      ...serializeError(err),
    });
    supportCache = false;
  }
  return supportCache;
}

/** Permissão atual do navegador (sem pedir). */
export function currentPermission(): PushPermission {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

/** Preferência do usuário (opt-out: ausente = ligado). */
export function getPref(): boolean {
  return lsGet(PREF_KEY) !== 'false';
}
/** Grava a preferência local (só este navegador). */
export function setPref(enabled: boolean): void {
  lsSet(PREF_KEY, enabled ? 'true' : 'false');
}

/** Instância única do FCM, criada sob demanda (só após `isWebPushSupported`). */
function messaging(): ReturnType<typeof getMessaging> {
  if (!messagingInstance) messagingInstance = getMessaging(firebaseApp);
  return messagingInstance;
}

/** Pede permissão se ainda `default` e devolve o token FCM (ou `null`). */
async function obtainToken(): Promise<{ token: string | null; permission: PushPermission }> {
  if (!(await isWebPushSupported())) return { token: null, permission: 'unsupported' };
  let permission = Notification.permission;
  if (permission === 'default') {
    trackEvents.pushPermissionPrompted();
    permission = await Notification.requestPermission();
    if (permission === 'denied') trackEvents.pushBlocked();
  }
  if (permission !== 'granted') return { token: null, permission };
  const token = await getToken(messaging(), { vapidKey: VAPID_KEY });
  return { token: token.length > 0 ? token : null, permission };
}

/** Desregistra no servidor o último token lembrado e esquece-o; falha só vai ao console. */
async function unregisterRemembered(): Promise<void> {
  const token = lsGet(LAST_TOKEN_KEY);
  if (token) {
    try {
      await unregisterAdminPushTokenCallable({ token });
    } catch (err) {
      console.warn(`${TAG}: unregister falhou`, err);
      // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
      reportNonFatal('admin_web_push_controller_failed', {
        site: 'unregister falhou',
        code: (err as { code?: string }).code ?? null,
        ...serializeError(err),
      });
    }
  }
  lsSet(LAST_TOKEN_KEY, null);
}

/**
 * Garante o registro do token conforme a preferência atual. Idempotente —
 * pode ser chamado em todo mount.
 *
 * Origem: sem issue — commit 7994b1111, 2026-05-12 (web push FCM do admin).
 *
 * Contrato: sem suporte/VAPID → `{ registered: false, permission:
 * 'unsupported' }`. Preferência desligada → desregistra o token lembrado. Se
 * o token do FCM mudou, desregistra o antigo e registra o novo pela callable
 * `registerAdminPushToken` (rejeita se ela falhar — o chamador trata). Pode
 * abrir o prompt de permissão do navegador (quando `default`).
 *
 * Usado por: `components/WebPushBootstrap.tsx` (mount do shell) e
 * `lib/useWebPush.ts` (toggle em `/settings`).
 *
 * Arquitetura: o token vai para `/adminPushTokens/{token}`, sem tenant —
 * super-admin não é membro de tenant. Escrita só server-side
 * (`apps/functions/src/admin/registerAdminPushToken.ts`).
 *
 * Dados — input da callable `registerAdminPushToken`:
 *   { "token": "<fcm-token>", "userAgent": "Mozilla/5.0 …" }
 * Lembrança local: `localStorage['etiquetachef.admin.webPush.token']`
 * (último token registrado) e `…webPush.enabled` (`'false'` = desligado).
 *
 * Dicas: a comparação com o token lembrado é o que evita uma callable por
 * mount; limpar o localStorage só força um novo registro (idempotente no
 * servidor).
 */
export async function syncToken(): Promise<{ registered: boolean; permission: PushPermission }> {
  if (!(await isWebPushSupported())) return { registered: false, permission: 'unsupported' };

  if (!getPref()) {
    await unregisterRemembered();
    return { registered: false, permission: currentPermission() };
  }

  const { token, permission } = await obtainToken();
  if (!token) {
    if (permission === 'denied') await unregisterRemembered();
    return { registered: false, permission };
  }

  const prevToken = lsGet(LAST_TOKEN_KEY);
  if (prevToken !== token) {
    if (prevToken) {
      try {
        await unregisterAdminPushTokenCallable({ token: prevToken });
      } catch (err) {
        console.warn(`${TAG}: unregister do token anterior falhou`, err);
        // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
        reportNonFatal('admin_web_push_controller_failed', {
          site: 'unregister do token anterior falhou',
          code: (err as { code?: string }).code ?? null,
          ...serializeError(err),
        });
      }
    }
    await registerAdminPushTokenCallable({
      token,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    });
    lsSet(LAST_TOKEN_KEY, token);
    if (!prevToken) trackEvents.pushEnabled();
  }
  return { registered: true, permission };
}

/** Desliga: desregistra o token lembrado e apaga o token local do FCM. */
export async function disablePush(): Promise<void> {
  await unregisterRemembered();
  if (await isWebPushSupported()) {
    try {
      await deleteToken(messaging());
    } catch (err) {
      console.warn(`${TAG}: deleteToken falhou`, err);
      // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
      reportNonFatal('admin_web_push_controller_failed', {
        site: 'deleteToken falhou',
        code: (err as { code?: string }).code ?? null,
        ...serializeError(err),
      });
    }
  }
}

/** Unsubscribe vazio para quando não há assinatura (sem suporte ou falha). */
const NOOP_UNSUBSCRIBE = (): void => {
  /* sem assinatura ativa */
};

/** Assina mensagens em foreground. Devolve unsubscribe (no-op se sem suporte). */
export async function subscribeForeground(
  cb: (payload: MessagePayload) => void,
): Promise<() => void> {
  if (!(await isWebPushSupported())) return NOOP_UNSUBSCRIBE;
  try {
    return onMessage(messaging(), cb);
  } catch (err) {
    console.warn(`${TAG}: onMessage falhou`, err);
    // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
    reportNonFatal('admin_web_push_controller_failed', {
      site: 'onMessage falhou',
      code: (err as { code?: string }).code ?? null,
      ...serializeError(err),
    });
    return NOOP_UNSUBSCRIBE;
  }
}
